import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import scoreColors from 'global/styles/scoreColors';
import { CircularProgress } from '@mui/material';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { get } from 'global/lib/lodash';

const useMobileScreen = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('lg'));
  return !matches;
};

const useMediaMatch = (str = 'lg') => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up(str));
  return matches;
};

const useMediaMatchDown = (str = 'lg') => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(str));
  return matches;
};

const useMobileScreenXS = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  return !matches;
};

export const useScreenDims = () => {
  return { width: window.innerWidth, height: window.innerHeight }
}

//only use this out of all the mediaquery stuff
export const useScreen = (str = 'lg') => {
  const isUp = useMediaMatch(str);
  const isDown = useMediaMatchDown(str);
  const isMobile = useMobileScreen();
  const isMobileXS = useMobileScreenXS();
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

  return {
    isUp,
    isDown,
    isMobile,
    isMobileXS,
    isTouchDevice,
  };
};

export const isDark = (theme) => {
  return get(theme, 'palette.mode') === 'dark';
};

export const grey = (theme, val) => {
  return get(theme, `palette.grey.${val}`);
};

export const getColorFromValue = (value) => {
  // expects a value between 0 and 100 or -1
  const val = (() => {
    if (value < 0) {
      return '';
    } else {
      return Math.floor(value / 10);
    }
  })();
  return scoreColors[`score${val}`];
};

export const getColorFromScore = (score, minMax, isBw = false) => {
  // expects a score between 0 and 100 or -1
  const def = [0, 100];
  const ranges = getAccountSetting(SETTINGS.assessmentColorScale);
  const pickRanges = Array.isArray(minMax) ? minMax : Array.isArray(ranges) ? ranges : def;
  const [min, max] = pickRanges;
  const val = (() => {
    if (score == undefined) {
      return 'none';
    } else if (score < 0) {
      return '';
    } else if (score <= min) {
      return 0;
    } else if (score >= max) {
      return 10;
    } else {
      return Math.floor(((score - min) * (100 / (max - min))) / 10);
    }
  })();
  return scoreColors[`score${isBw ? 'bw' : ''}${val}`];
};

export const getSimpleColorFromScore = (score) => {
  score = get(score, 'value', score);

  const colors = {
    good: `112, 192, 55`,
    bad: `214, 50, 22`,
  }

  if (score == 'N/A') { return 'rgba(0,0,0,0)' }

  const perc = Math.abs(score) / 8;

  return `rgba(${score > 0 ? colors.good : colors.bad}, ${perc})`;
};

export const getInstanceColorFromScore = (score, isBw = false) => {
  const val = (() => {
    if (score === 0) {
      return 2;
    } else if (score === 100) {
      return 10;
    } else {
      return 4;
    }
  })();

  return scoreColors[`score${isBw ? 'bw' : ''}${val}`];
};

export const classificationToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let value = (hash >> 8) & 0xff;
  if (value < 120) {
    value = value + 120;
  }
  if (value > 200) {
    value = value - 55;
  }
  let colour = '#';
  for (let x = 0; x < 3; x++) {
    colour += ('FF' + value.toString(16)).substr(-2);
  }
  return colour;
};

export const screenDims = () => {
  return {
    ...window.screen,
  };
};

export const imageStyle = (dim = '100%') => {
  return { maxWidth: dim, maxHeight: dim, height: 'auto', width: 'auto' };
};

export const Loading = <CircularProgress size={12} />;

export const Loader = (props) => <CircularProgress {...props} />;

// ---------------------------------------------------------------------------
// Global style classes (formerly `makeStyles` default export). Following the
// same pattern as `global/styles/filters.js`: stable className strings backed
// by CSS injected once at module load. Existing consumers keep their
// `Styles().centerColumn` etc. usage.
// ---------------------------------------------------------------------------

const PREFIX = 'globalStyles';

const classes = {
  centerColumn: `${PREFIX}-centerColumn`,
  row: `${PREFIX}-row`,
  imageWrap: `${PREFIX}-imageWrap`,
  clickableImage: `${PREFIX}-clickableImage`,
  checkIcon: `${PREFIX}-checkIcon`,
  checkIconSelected: `${PREFIX}-checkIconSelected`,
  formContainer: `${PREFIX}-formContainer`,
  printOnly: `${PREFIX}-printOnly`,
  processingContainer: `${PREFIX}-processingContainer`,
  imageListItem: `${PREFIX}-imageListItem`,
  imgCaption: `${PREFIX}-imgCaption`,
  select: `${PREFIX}-select`,
  printLogoContainer: `${PREFIX}-printLogoContainer`,
  imageCarousel: `${PREFIX}-imageCarousel`,
  linkSelect: `${PREFIX}-linkSelect`,
};

// Re-inject when the theme tokens we depend on change. This keeps account-
// settings overrides (primary/secondary) live without forcing consumers to
// migrate off the className API.
let injectedHash = null;
const themeHash = (theme) =>
  [
    theme.palette.secondary.main,
    theme.palette.primary.main,
    theme.palette.grey?.['200'],
    theme.palette.grey?.['500'],
    theme.palette.common?.white,
    theme.palette.common?.black,
    theme.breakpoints?.values?.xs,
    theme.breakpoints?.values?.lg,
  ].join('|');

const injectStyles = (theme) => {
  if (typeof document === 'undefined') return;
  const hash = themeHash(theme);
  if (injectedHash === hash) return;
  const prev = document.getElementById(`${PREFIX}-styles`);
  if (prev) prev.remove();
  injectedHash = hash;

  const css = `
    .${PREFIX}-centerColumn {
      flex-direction: column;
      align-items: center;
      justify-content: center;
      display: flex;
      height: 100%;
      flex: 1 1 auto;
    }
    .${PREFIX}-row {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .${PREFIX}-imageWrap {
      page-break-inside: avoid !important;
    }
    .${PREFIX}-imageWrap > div {
      background-color: transparent !important;
      display: flex;
      height: 100%;
      padding-top: 0 !important;
    }
    .${PREFIX}-imageWrap > div img {
      width: auto !important;
      height: auto !important;
      max-width: 100%;
      max-height: 100%;
      top: auto !important;
      left: auto !important;
    }
    .${PREFIX}-clickableImage.IMAGE {
      position: relative;
      cursor: pointer;
    }
    .${PREFIX}-clickableImage.IMAGE:hover {
      opacity: 0.9;
    }
    .${PREFIX}-checkIcon {
      position: absolute;
      top: 0;
      left: 0;
    }
    .${PREFIX}-checkIconSelected {
      color: ${theme.palette.common.black};
    }
    .${PREFIX}-checkIconSelected svg {
      background-color: ${theme.palette.common.white};
    }
    .${PREFIX}-formContainer {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 0 auto;
    }
    @media (min-width: ${theme.breakpoints.values.xs}px) and (max-width: ${theme.breakpoints.values.lg - 0.05}px) {
      .${PREFIX}-formContainer { width: auto; }
    }
    .${PREFIX}-formContainer .MuiInputBase-root { width: 100%; }
    .${PREFIX}-formContainer .MuiFormGroup-root {
      background-color: ${theme.palette.grey['200']};
      width: 85%;
      margin-top: ${theme.spacing(3)};
      padding: 0 ${theme.spacing(1)};
    }
    .${PREFIX}-printOnly { display: none; }
    @media print {
      .${PREFIX}-printOnly { display: block; }
    }
    .${PREFIX}-processingContainer {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .${PREFIX}-processingContainer .processText {
      font-size: 12px;
      margin-top: ${theme.spacing(1)};
    }
    .${PREFIX}-imageListItem {
      display: flex;
      flex-direction: column;
      page-break-inside: avoid;
    }
    .${PREFIX}-imgCaption {
      font-size: 10px;
      color: ${theme.palette.grey['500']};
      padding: 1px;
    }
    .${PREFIX}-select {
      border-radius: 4px;
      margin-left: ${theme.spacing(1)};
    }
    .${PREFIX}-select .MuiFilledInput-input {
      padding: 5px 12px 6px;
      padding-right: 32px;
    }
    .${PREFIX}-printLogoContainer .IMAGE .mui-image-wrapper {
      justify-content: flex-start !important;
    }
    .${PREFIX}-imageCarousel .imageItem {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .${PREFIX}-linkSelect {
      color: ${theme.palette.secondary.main};
      text-transform: none;
    }
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

const Styles = () => {
  const theme = useTheme();
  injectStyles(theme);
  return classes;
};

export const CenterLoader = (props) => {
  const cls = Styles();

  return (
    <div {...props} className={cls.centerColumn}>
      <Loader {...props} />
    </div>
  );
};

export default Styles;
